// The machine's voice, and the switch that turns it off.
//
// SETUP has always offered PLAIN ENGLISH, and the silkscreen under it has
// always promised "PLAIN ENGLISH DROPS THE SHOUTING". Nothing read the flag, so
// the switch moved and the app carried on shouting — a setting that lies is
// worse than a setting that is missing, because the person who needed it now
// believes they have tried the thing that was meant to help.
//
// The machine's clipped uppercase is the joke. It is also the register that
// misled six people in the persona pass, and it is unreadable to anyone who
// relies on word shape. So there are two strings for every line the machine
// itself speaks, and this decides which one you get.
//
// SILKSCREEN IS NOT THE MACHINE. The printed legends on the case stay uppercase
// in both modes: the factory printed them, they are already plain, and they are
// the one thing that never breaks character.

import { computed, watchEffect } from 'vue';
import { machine } from './machine.js';

export const plainEnglish = computed(() => !!machine.value.plainEnglish);

// HALF THE SHOUTING IS CSS, so swapping the strings is only half the switch.
// `.tray-title`, `.lcd`, `.press` and the CONDITION positions are all
// `text-transform: uppercase` with wide tracking, so `say('READY', 'Ready.')`
// still came out as "READY." until this class existed. One class on the root
// element, one rule in app.css, and it cannot get out of step with `say()`
// because both read the same flag.
if (typeof document !== 'undefined') {
  watchEffect(() => {
    document.documentElement.classList.toggle('plain-english', plainEnglish.value);
  });
}

/**
 * Pick the register.
 *
 * @param {string} shouted  what the machine says
 * @param {string} plain    the same fact, in a sentence
 */
export const say = (shouted, plain) => (plainEnglish.value ? plain : shouted);

/**
 * One clause, punctuated exactly once.
 *
 * Image descriptions are assembled from fields somebody typed, and a memo that
 * already ended in a full stop came out as "She ate the report.. A cover
 * sheet." VoiceOver reads a doubled stop as a pause long enough to sound like a
 * fault in the app.
 */
export const sentence = (s) => {
  const t = String(s ?? '').trim();
  if (!t) return '';
  return /[.!?…]$/.test(t) ? t : `${t}.`;
};

/** Several clauses, each punctuated once, empties dropped. */
export const sentences = (...parts) => parts.map(sentence).filter(Boolean).join(' ');
